
import React, { useState } from 'react';

const IntroductionPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('greeting');

  const scrollToSection = (sectionId: string) => {
    try {
      setActiveSection(sectionId);
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error scrolling to section:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* 고정 네비게이션 */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-orange-800">천축선원 소개</h1>
            <div className="flex space-x-6">
              {[
                { id: 'greeting', label: '인사말' },
                { id: 'history', label: '천축선원의 역사' },
                { id: 'meaning', label: '기원정사의 의미' },
                { id: 'location', label: '위치 / 오시는 길' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                    activeSection === item.id
                      ? 'bg-orange-600 text-white'
                      : 'text-orange-700 hover:bg-orange-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* 메인 콘텐츠 */}
      <div className="pt-24">
        {/* 주지스님 인사말 */}
        <section id="greeting" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-orange-800 mb-4">주지스님 인사말씀</h2>
              <div className="w-24 h-1 bg-orange-600 mx-auto"></div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* 주지스님 사진 */}
              <div className="text-center">
                <div className="inline-block">
                  <img
                    src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/0fa102fc8e6bfe00e5f16b545d837845.jpeg"
                    alt="주지 대인동심 스님"
                    className="w-80 h-96 object-right object-cover rounded-lg shadow-lg"
                  />
                  <p className="text-orange-800 font-semibold mt-4 text-lg">주지 대인동심</p>
                </div>
              </div>

              {/* 인사말 내용 */}
              <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-600">
                  <p className="text-lg leading-relaxed text-gray-800">
                    삼계 도사이시며 사생의 자부이신 부처님께 예경하옵니다.<br/>
                    또한 불적지 순례자와 모든 수행자들께 공경의 마음을 다합니다.
                  </p>
                </div>

                <blockquote className="text-center italic text-orange-700 text-xl border-t border-b border-orange-200 py-4">
                  "성인이 가신지 오래되어 영원한 생명의 길로 가는 이는 적고,<br/>
                  사법이 난무하여 생사의 고통이 끝이 없네."
                </blockquote>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    부처님의 광명이 서린 곳 인도 땅 쉬라바스티(기원정사), 부처님 재세시 25안거를 나시면서, 
                    금강경, 원각경, 능엄경 등 대승경전의 대부분을 설하시고 무량중생을 제도하신 곳!
                  </p>
                  
                  <p className="font-semibold text-orange-800">
                    이 성스러운 땅에 천축선원을 세우는 까닭은 무엇인가?
                  </p>
                  
                  <p>
                    세세생생 부처님의 밝음을 향하던 수행자들이 모여들어 무시겁 업보 닦고 자성을 철견해서 
                    부처님의 혜명을 이어가는 정법도량을 이루리라. 이것이 부처님과 조사의 부촉이라.
                  </p>
                  
                  <p>
                    부처님 법은(진리의 은혜)를 크게 입은 제자들이 어찌 신명을 아끼리오!
                  </p>
                  
                  <p>
                    천축선원 수행도량에 세계의 발심행자들이 모여 조사선을 참구하여 
                    <strong className="text-orange-800"> 明心見性 (마음을 밝혀 자기 성품을 보다)</strong>는 
                    무아법을 통탈한 수행자들이 나와 그 옛날 거룩했던 영산회상이 재현되길 발원하네.
                  </p>
                  
                  <div className="bg-gradient-to-r from-orange-100 to-yellow-50 p-6 rounded-lg mt-6">
                    <p className="text-center font-semibold text-orange-800 mb-2">
                      공경하옵는 사부 대중이시여!
                    </p>
                    <p className="text-center">
                      기원정사 여래향실 부처님의 "금강반야회상의 깨달음의 향기를 세계 만방에 전합시다.
                    </p>
                  </div>
                  
                  <div className="text-right mt-6">
                    <p className="text-orange-800 font-semibold">
                      기원정사 천축선원 주지 대인동심 합장
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 천축선원의 역사 */}
        <section id="history" className="py-16 bg-orange-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-orange-800 mb-4">천축선원 창건 연혁</h2>
              <div className="w-24 h-1 bg-orange-600 mx-auto mb-6"></div>
              <p className="text-xl text-gray-700 max-w-4xl mx-auto">
                한국 불교의 가르침을 세계에 밝히고 지역 공동체와 함께하는 수행·교육·복지 도량
              </p>
            </div>

            {/* 개요 */}
            <div className="bg-white p-8 rounded-lg shadow-lg mb-12">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                천축선원은 1999년 인도 불교 성지 스라와스티에 원력을 세우고 입국한 대인 스님을 중심으로, 
                한국 불교의 가르침을 세계에 밝히고 지역 공동체와 함께하는 수행·교육·복지 도량으로 자리매김해 왔습니다.
              </p>
            </div>

            {/* 상세 연혁 */}
            <div className="space-y-12">
              
              {/* 창건기 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-orange-600 to-amber-600 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center">
                    <i className="ri-seedling-line text-3xl mr-3"></i>
                    창건기 (1999~2000)
                  </h3>
                </div>
                <div className="p-8">
                  <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <img
                        src="https://public.readdy.ai/ai/img_res/48b075f66475895b7a01ba0731fda606.jpg"
                        alt="천축선원 창건기"
                        className="w-full h-64 object-cover rounded-lg shadow-md"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="border-l-4 border-orange-500 pl-6">
                        <h4 className="font-bold text-orange-800 mb-2">1999년 10월 2일</h4>
                        <p className="text-gray-700">대인 스님 인도 입국, 스라와스티 지역 포교 원력 발원</p>
                      </div>
                      <div className="border-l-4 border-orange-500 pl-6">
                        <h4 className="font-bold text-orange-800 mb-2">1999년 11월</h4>
                        <p className="text-gray-700">불사 부지 매입</p>
                      </div>
                      <div className="border-l-4 border-orange-500 pl-6">
                        <h4 className="font-bold text-orange-800 mb-2">2000년 2월</h4>
                        <p className="text-gray-700">임시 원두막 법당과 사무실, 수행 처소, 주방 등을 갖춘 초기 도량 조성</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 초기 사회지원기 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center">
                    <i className="ri-home-heart-line text-3xl mr-3"></i>
                    초기 사회지원기 (2003~2013)
                  </h3>
                </div>
                <div className="p-8">
                  <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div className="space-y-4">
                      <p className="text-lg text-gray-700 leading-relaxed">
                        천축선원은 출가 수행처를 넘어 지역 이웃과 함께하는 도량을 지향하였습니다.
                      </p>
                      <div className="bg-blue-50 p-6 rounded-lg">
                        <h4 className="font-bold text-blue-800 mb-3">벽돌집 건축 지원 사업</h4>
                        <p className="text-gray-700">
                          2003년부터 2013년까지 10년간, 스라와스티 빈곤 가정의 벽돌집 건축 지원 사업을 전개하며 
                          지역 사회 기반을 튼튼히 하고 생계를 돕는 민생 지원 활동을 펼쳤습니다.
                        </p>
                      </div>
                    </div>
                    <div>
                      <img
                        src="https://readdy.ai/api/search-image?query=Buddhist%20monks%20helping%20build%20brick%20houses%20for%20poor%20families%20in%20rural%20India%2C%20community%20service%20work%2C%20traditional%20Indian%20village%20setting%2C%20monks%20in%20orange%20robes%20working%20alongside%20local%20people%2C%20construction%20of%20simple%20brick%20homes&width=500&height=350&seq=social-support-1&orientation=landscape"
                        alt="벽돌집 건축 지원"
                        className="w-full h-64 object-cover rounded-lg shadow-md"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 지역 의료복지 확장기 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center">
                    <i className="ri-hospital-line text-3xl mr-3"></i>
                    지역 의료복지 확장기 (2005)
                  </h3>
                </div>
                <div className="p-8">
                  <div className="grid lg:grid-cols-2 gap-8 items-center">
                    <div>
                      <img
                        src="https://readdy.ai/api/search-image?query=Small%20rural%20health%20clinic%20in%20India%20built%20by%20Buddhist%20temple%2C%20medical%20facility%20with%20simple%20architecture%2C%20monks%20providing%20healthcare%20services%2C%20local%20Indian%20villagers%20receiving%20medical%20care%2C%20community%20health%20center&width=500&height=350&seq=medical-welfare-1&orientation=landscape"
                        alt="지역 보건소"
                        className="w-full h-64 object-cover rounded-lg shadow-md"
                      />
                    </div>
                    <div className="space-y-4">
                      <div className="bg-green-50 p-6 rounded-lg">
                        <h4 className="font-bold text-green-800 mb-3">지역 보건소 건립</h4>
                        <p className="text-gray-700 mb-4">
                          2005년, 지구촌공동회의 보시에 힘입어 지역 보건소를 건립하고 의약품을 지원하였습니다.
                        </p>
                        <p className="text-gray-700">
                          이는 의료 접근성이 낮은 지역 주민들에게 큰 도움을 주며 천축선원의 사회복지 활동이 
                          확장되는 전환점이 되었습니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 도량 불상 조성기 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-violet-600 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center">
                    <i className="ri-ancient-pavilion-line text-3xl mr-3"></i>
                    도량 불상 조성기 (2014)
                  </h3>
                </div>
                <div className="p-8">
                  <div className="grid lg:grid-cols-2 gap-8 items-center mb-8">
                    <div className="space-y-6">
                      <p className="text-xl text-gray-700 leading-relaxed font-medium">
                        천축선원은 수행·예경·순례의 거점으로 나아가기 위해 2014년, 대규모 불사를 진행하였습니다.
                      </p>
                      <div className="space-y-4">
                        <div className="bg-purple-50 p-6 rounded-lg">
                          <h5 className="font-bold text-purple-800 mb-3 text-lg">주요 불사 내용</h5>
                          <ul className="space-y-3 text-gray-700 text-lg">
                            <li>• 한국 화강암으로 제작된 7m 석가모니불 입상 조성</li>
                            <li>• 석등 2기 설치</li>
                            <li>• 큰법당 삼존불 마련</li>
                          </ul>
                        </div>
                        <div className="bg-amber-50 p-6 rounded-lg">
                          <h5 className="font-bold text-amber-800 mb-3 text-lg">동참 현황</h5>
                          <p className="text-gray-700 text-lg leading-relaxed">
                            한국 아차산 기원정사의 설봉 스님, 비구니회 보현회원 스님들을 비롯한 
                            많은 국내외 사부대중의 원력과 동참으로 성취되었으며, 성대하고 장엄한 봉불식이 봉행되었습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <img
                          src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/9e1d3b821d24127000a8099816270a40.jpeg"
                          alt="7m 석가모니불 입상"
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                        <p className="text-center text-sm text-gray-600 mt-2">7m 석가모니불 입상</p>
                      </div>
                      <div>
                        <img
                          src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/f2ccd13674f24bb50048463e13e0ef35.jpeg"
                          alt="큰법당 삼존불"
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                        <p className="text-center text-sm text-gray-600 mt-2">큰법당 삼존불</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* 봉불식 기념 사진들 */}
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <img
                        src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/fcb76432970877e600faa5e7ab28445a.jpeg"
                        alt="봉불식 기념 사진"
                        className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
                      />
                      <p className="text-center text-sm text-gray-600 mt-3 font-medium">봉불식 기념 사진</p>
                    </div>
                    <div>
                      <img
                        src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/6fbfb4be71478dac69558ae0f4f59a9f.jpeg"
                        alt="봉불식 기념 사진"
                        className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
                      />
                      <p className="text-center text-sm text-gray-600 mt-3 font-medium">봉불식 기념 사진</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 교육·문화 불사기 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-6">
                  <h3 className="text-2xl font-bold text-white flex items-center">
                    <i className="ri-school-line text-3xl mr-3"></i>
                    교육·문화 불사기 (2016~현재)
                  </h3>
                </div>
                <div className="p-8 space-y-8">
                  
                  {/* 2016년 */}
                  <div className="border-l-4 border-amber-500 pl-6">
                    <h4 className="text-xl font-bold text-amber-800 mb-4">2016년 — 보광초·중학교 설립</h4>
                    <div className="grid lg:grid-cols-2 gap-6">
                      <div>
                        <img
                          src="https://readdy.ai/api/search-image?query=Modern%20school%20building%20in%20rural%20India%20built%20by%20Buddhist%20temple%2C%20children%20in%20school%20uniforms%20studying%2C%20library%20and%20audiovisual%20room%2C%20educational%20facilities%20with%20Korean%20Buddhist%20temple%20support%2C%20happy%20Indian%20students%20learning&width=400&height=300&seq=school-establishment-1&orientation=landscape"
                          alt="보광학교"
                          className="w-full h-48 object-cover rounded-lg shadow-md"
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="bg-amber-50 p-4 rounded-lg">
                          <p className="text-gray-700">
                            <strong>승가대학장 보각 스님</strong>의 발원과 한국 불자들의 십일반 동참으로 
                            천축선원 내 <strong>보광학교(초·중학교)</strong>가 건립되었습니다.
                          </p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-gray-700">
                            한국 <strong>세무사회(정구정 회장)</strong>의 보시로 도서관과 시청각실이 개원하였습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 2017년 */}
                  <div className="border-l-4 border-blue-500 pl-6">
                    <h4 className="text-xl font-bold text-blue-800 mb-4">2017년 — 지역 교육·복지 확대</h4>
                    <div className="grid lg:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h5 className="font-bold text-blue-800 mb-2">정수기 설치 사업</h5>
                          <p className="text-gray-700">
                            봉화 향적사 선진 스님과 신도들의 보시로 정수기 10대가 
                            스라와스티 지역 10개 학교에 설치되었습니다.
                          </p>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h5 className="font-bold text-green-800 mb-2">학제 운영 시작</h5>
                          <p className="text-gray-700">
                            보광학교는 신입생 20명을 맞이하며 1학년부터 8학년까지 학제 운영을 시작하였습니다.
                          </p>
                        </div>
                      </div>
                      <div>
                        <img
                          src="https://readdy.ai/api/search-image?query=Water%20purification%20systems%20installed%20in%20rural%20Indian%20schools%2C%20clean%20drinking%20water%20facilities%2C%20students%20using%20water%20purifiers%2C%20educational%20infrastructure%20improvement%2C%20community%20development%20project&width=400&height=300&seq=water-purifier-1&orientation=landscape"
                          alt="정수기 설치"
                          className="w-full h-48 object-cover rounded-lg shadow-md"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 2025년 현재 */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <h4 className="text-xl font-bold text-green-800 mb-4">2025년 — 현재</h4>
                    <div className="grid lg:grid-cols-2 gap-6">
                      <div>
                        <img
                          src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/d1fc1f2266ca2f8a8fd3ac5224148b6e.jpeg"
                          alt="현재 천축선원"
                          className="w-full h-48 object-cover rounded-lg shadow-md"
                        />
                      </div>
                      <div className="space-y-3">
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h5 className="font-bold text-green-800 mb-2">보광학교 성장</h5>
                          <p className="text-gray-700">
                            보광학교는 총 <strong>160명의 재학생</strong>이 공부하는 지역 대표 교육기관으로 성장하였습니다.
                          </p>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <h5 className="font-bold text-purple-800 mb-2">국제 순례 도량</h5>
                          <p className="text-gray-700">
                            연간 약 <strong>18,000여 명의 순례객</strong>이 세계 각국(스리랑카, 태국, 베트남, 캄보디아, 
                            인도, 유럽, 미국, 멕시코)에서 찾아오는 국제 수행·순례 도량으로 자리잡았습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 통계 요약 */}
              <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-orange-800 mb-6 text-center">천축선원 26년간의 성과</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-time-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-3xl font-bold text-orange-800">26년</h4>
                    <p className="text-gray-700">창건 역사</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-group-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-3xl font-bold text-blue-800">18,000명</h4>
                    <p className="text-gray-700">연간 순례객</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-graduation-cap-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-3xl font-bold text-green-800">160명</h4>
                    <p className="text-gray-700">보광학교 재학생</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-global-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-3xl font-bold text-purple-800">12개국</h4>
                    <p className="text-gray-700">순례객 출신국</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 기원정사의 의미 */}
        <section id="meaning" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-orange-800 mb-4">기원정사의 의미</h2>
              <div className="w-24 h-1 bg-orange-600 mx-auto mb-6"></div>
              <p className="text-xl text-gray-700">25안거 성지의 역사적 의미</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-orange-100 to-yellow-50 p-8 rounded-lg text-center">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-open-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-orange-800 mb-3">대승경전의 설법지</h3>
                <p className="text-gray-700">금강경, 원각경, 능엄경 등 대승경전의 대부분이 이곳에서 설해졌습니다.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-100 to-yellow-50 p-8 rounded-lg text-center">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-orange-800 mb-3">25안거 성지</h3>
                <p className="text-gray-700">부처님께서 25안거를 나시며 무량중생을 제도하신 성스러운 땅입니다.</p>
              </div>

              <div className="bg-gradient-to-br from-orange-100 to-yellow-50 p-8 rounded-lg text-center">
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-orange-800 mb-3">국제 포교 중심지</h3>
                <p className="text-gray-700">전 세계 수행자들이 모여 정법을 구하는 국제적인 수행 도량입니다.</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-orange-800 mb-6 text-center">기원정사 여래향실</h3>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img
                    src="https://static.readdy.ai/image/bb60b64d4d14d0ba531f646d8a4266d5/a69d780ffcf5f4f01b91ed6a2a285a33.jpeg"
                    alt="기원정사 여래향실"
                    className="w-full h-64 object-cover rounded-lg shadow-md"
                  />
                </div>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    부처님께서 머무르시며 법을 설하신 여래향실은 모든 불자들에게 가장 성스러운 공간입니다. 
                    이곳에서 금강반야회상의 깨달음이 펼쳐졌으며, 지금도 그 법향이 세계 만방에 전해지고 있습니다.
                  </p>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-orange-800 font-semibold text-center">
                      "금강반야회상의 깨달음의 향기를 세계 만방에"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 위치 / 오시는 길 */}
        <section id="location" className="py-16 bg-orange-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-orange-800 mb-4">위치 / 오시는 길</h2>
              <div className="w-24 h-1 bg-orange-600 mx-auto mb-6"></div>
              <p className="text-xl text-gray-700">인도 우타르프라데시주 쉬라바스티</p>
            </div>

            {/* 구글 맵 */}
            <div className="mb-12">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-orange-800 mb-4 text-center">천축선원 위치</h3>
                <div className="aspect-video w-full rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3563.7!2d82.04167!3d27.50667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3998a1234567890a%3A0x1234567890abcdef!2sKorean%20Temple%20Shravasti!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="천축선원 위치"
                  ></iframe>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-gray-700">
                    <strong>주소:</strong> Korean temple near Japan bell, Khargupur, Uttar Pradesh 271805, India
                  </p>
                </div>
              </div>
            </div>

            {/* 연락처 정보 */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center">
                  <i className="ri-phone-line text-2xl mr-3"></i>
                  연락처
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🇮🇳</span>
                    <div>
                      <p className="font-semibold">+91-892-313-9035</p>
                      <p className="text-gray-600 text-sm">(총괄 메니저 와르단)</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🇮🇳</span>
                    <div>
                      <p className="font-semibold">+91-799-175-5124</p>
                      <p className="text-gray-600 text-sm">(주지스님)</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🇰🇷</span>
                    <div>
                      <p className="font-semibold">+82-010-6508-1961</p>
                      <p className="text-gray-600 text-sm">(총무 적조행)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center">
                  <i className="ri-time-line text-2xl mr-3"></i>
                  참배 시간
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span>오전 예불</span>
                    <span className="font-semibold">04:00 - 06:00</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>오후 예불</span>
                    <span className="font-semibold">18:00 - 19:00</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>점심시간</span>
                    <span className="font-semibold">12:00 - 13:00</span>
                  </div>
                  <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm font-semibold text-orange-800 mb-2">
                      방문 전 연락 필수
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🇮🇳</span>
                      <div>
                        <p className="font-semibold text-gray-800">+91-892-313-9035</p>
                        <p className="text-xs text-gray-600">(총괄 메니저 와르단)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 교통편 안내 */}
            <div className="space-y-8">
              <h3 className="text-3xl font-bold text-orange-800 text-center mb-8">교통편 안내</h3>

              {/* 비행기 */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-plane-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-800">항공편</h4>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-bold text-lg text-blue-800 mb-3">국제선</h5>
                    <div className="space-y-2">
                      <p><strong>델리 인디라 간디 국제공항 (DEL)</strong></p>
                      <p className="text-gray-600">• 한국에서 직항 또는 경유편 이용</p>
                      <p className="text-gray-600">• 공항에서 쉬라바스티까지 약 6-7시간 (차량)</p>
                      <p className="text-gray-600">• 대한항공, 아시아나항공, 에어인디아 운항</p>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-bold text-lg text-blue-800 mb-3">국내선 연결</h5>
                    <div className="space-y-2">
                      <p><strong>럭나우 공항 (LKO)</strong></p>
                      <p className="text-gray-600">• 델리에서 국내선 연결 (1시간 30분)</p>
                      <p className="text-gray-600">• 럭나우에서 쉬라바스티까지 약 3시간</p>
                      <p className="text-gray-600">• IndiGo, SpiceJet 등 운항</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 철도 */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-train-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-800">철도</h4>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-bold text-lg text-green-800 mb-3">주요 노선</h5>
                    <div className="space-y-3">
                      <div className="border-l-4 border-green-500 pl-4">
                        <p><strong>발람푸르 정션 (BLP)</strong></p>
                        <p className="text-gray-600">• 쉬라바스티에서 가장 가까운 기차역 (18km)</p>
                        <p className="text-gray-600">• 델리-발람푸르: 약 8-10시간</p>
                      </div>
                      <div className="border-l-4 border-green-500 pl-4">
                        <p><strong>곤다 정션 (GD)</strong></p>
                        <p className="text-gray-600">• 쉬라바스티에서 35km</p>
                        <p className="text-gray-600">• 더 많은 열차 운행</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-bold text-lg text-green-800 mb-3">추천 열차</h5>
                    <div className="space-y-2">
                      <p><strong>Shramjeevi Express (15273/15274)</strong></p>
                      <p className="text-gray-600">• 델리-발람푸르 직통</p>
                      <p className="text-gray-600">• 출발: 델리 22:15 → 도착: 발람푸르 06:30</p>
                      <p className="text-gray-600">• AC 2Tier, AC 3Tier 이용 가능</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 버스 */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-bus-line text-2xl text-white"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-800">버스</h4>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-bold text-lg text-orange-800 mb-3">정부 버스</h5>
                    <div className="space-y-2">
                      <p><strong>델리에서 출발</strong></p>
                      <p className="text-gray-600">• 거리: 약 550km</p>
                      <p className="text-gray-600">• 소요시간: 6-7시간</p>
                      <p className="text-gray-600">• 경로: NH27 → NH330</p>
                      <p className="text-gray-600">• 택시 요금: ₹8,000-12,000</p>
                    </div>
                  </div>
                  <div>
                    <h5 className="font-bold text-lg text-orange-800 mb-3">추천 경로</h5>
                    <div className="space-y-2">
                      <p><strong>델리 → 럭나우 → 쉬라바스티</strong></p>
                      <p className="text-gray-600">• 고속도로 이용 (더 안전)</p>
                      <p className="text-gray-600">• 중간 휴게소: 바레일리, 럭나우</p>
                      <p className="text-gray-600">• Ola, Uber 장거리 예약 가능</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 현지 교통 */}
              <div className="bg-gradient-to-r from-orange-100 to-yellow-50 p-8 rounded-lg">
                <h4 className="text-2xl font-bold text-orange-800 mb-6 text-center">쉬라바스티 현지 교통</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-taxi-line text-2xl text-white"></i>
                    </div>
                    <h5 className="font-bold text-orange-800 mb-2">오토 릭샤</h5>
                    <p className="text-gray-700 text-sm">시내 이동 주요 수단<br/>요금: ₹20-50</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-bike-line text-2xl text-white"></i>
                    </div>
                    <h5 className="font-bold text-orange-800 mb-2">사이클 릭샤</h5>
                    <p className="text-gray-700 text-sm">단거리 이동<br/>요금: ₹10-30</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-walk-line text-2xl text-white"></i>
                    </div>
                    <h5 className="font-bold text-orange-800 mb-2">도보</h5>
                    <p className="text-gray-700 text-sm">유적지 간 이동<br/>천축선원에서 기원정사까지 도보 10분</p>
                  </div>
                </div>
              </div>

              {/* 여행 팁 */}
              <div className="bg-blue-50 p-8 rounded-lg">
                <h4 className="text-2xl font-bold text-blue-800 mb-6 text-center">여행 팁</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-bold text-blue-800 mb-3">준비사항</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li>• 인도 비자 필수 (e-Visa 가능)</li>
                      <li>• 황열병 예방접종 권장</li>
                      <li>• 현금 준비 (ATM 제한적)</li>
                      <li>• 편안한 신발, 모자 필수</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-bold text-blue-800 mb-3">최적 방문 시기</h5>
                    <ul className="space-y-2 text-gray-700">
                      <li>• 10월-3월: 선선한 날씨</li>
                      <li>• 4월-6월: 더위 주의</li>
                      <li>• 7월-9월: 몬순 시즌</li>
                      <li>• 부처님 오신 날 특별 법회</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default IntroductionPage;
